import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Toast from '../components/Toast';
import Breadcrumbs from '../components/Breadcrumbs';
import { normalizePhone, normalizeEmail } from '../utils/validationUtils';

const HelpForm = () => {
  const [helpTypes, setHelpTypes] = useState([]);
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [specificHelp, setSpecificHelp] = useState('');
  const [events, setEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [availability, setAvailability] = useState(null);
  const submittedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubAvail = onSnapshot(doc(db, 'availability', 'settings'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        let showBanner = true;
        if (data.expirationDate) {
          const expireTime = data.expirationDate.toDate ? data.expirationDate.toDate() : new Date(data.expirationDate);
          if (new Date() > expireTime) {
            showBanner = false;
          }
        }
        setAvailability({
          ...data,
          showBanner: showBanner && data.status !== 'normal'
        });
      } else {
        setAvailability(null);
      }
    });

    const unsubscribeEvents = onSnapshot(
      query(collection(db, 'events'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setEvents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      },
      (error) => {
        console.error('Error fetching events:', error);
      }
    );

    return () => {
      unsubAvail();
      unsubscribeEvents();
    };
  }, []);

  const handleCheckboxChange = (type) => {
    setHelpTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting || submittedRef.current) return;

    if (helpTypes.length === 0) {
      setToast({ message: 'Please select at least one way you can help.', type: 'error' });
      return;
    }

    setSubmitting(true);
    submittedRef.current = true;

    try {
      const selectedEventData = events.find((ev) => ev.id === selectedEvent);
      const cleanEmail = normalizeEmail(email);
      const cleanPhone = normalizePhone(phone);
      await addDoc(collection(db, 'help_offers'), {
        types: helpTypes,
        details: details.trim() || null,
        email: cleanEmail,
        name: name.trim() || null,
        phone: cleanPhone,
        eventId: selectedEvent || null,
        eventName: selectedEventData
          ? String(selectedEventData.title || '').slice(0, 300)
          : selectedEvent === 'general'
            ? 'General Help'
            : null,
        specificHelp: specificHelp.trim() || null,
        status: 'new',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        schemaVersion: 2,
        notificationStatus: 'pending',
      });

      setToast({
        message: 'Thank you! Your help offer has been submitted successfully.',
        type: 'success',
      });

      setHelpTypes([]);
      setDetails('');
      setEmail('');
      setName('');
      setPhone('');
      setSelectedEvent('');
      setSpecificHelp('');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting help offer:', error);
      submittedRef.current = false;
      setToast({ message: 'Error submitting your offer. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const helpOptions = [
    'Donate Money',
    'Volunteer Time',
    'Make Food',
    'Purchase Items',
    'Other',
  ];

  const inputClass =
    'w-full px-4 py-3 bg-charcoal-card border border-white/10 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-hope-green focus:border-transparent';

  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />
      <Breadcrumbs />

      <main id="main-content" className="flex-grow max-w-2xl mx-auto w-full px-4 md:px-8 py-8">
        <div className="chrome-panel border border-white/5 rounded-lg p-6 md:p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 uppercase tracking-tight">
            How Would You Like to Help?
          </h2>

          {/* Availability Alert Banner */}
          {availability && availability.showBanner && (
            <div className={`mb-6 p-4 rounded-lg border text-xs leading-relaxed ${
              availability.status === 'unavailable' 
                ? 'bg-red-500/10 border-red-500/30 text-red-200' 
                : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200'
            }`}>
              <span className="font-bold uppercase tracking-wider block mb-1">
                ⚠️ Outreach Notice: {availability.status === 'unavailable' ? 'Service Offline' : 'Outreach Response Times May Be Delayed'}
              </span>
              <p>{availability.message}</p>
            </div>
          )}

          {!(availability && availability.acceptVolunteerOffers === true) ? (
            <div className="bg-charcoal-card border border-red-500/30 p-6 rounded-lg text-center space-y-4">
              <p className="text-red-200 font-bold uppercase tracking-wider text-xs">
                Volunteer Intake is Currently Closed
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                We are temporarily unable to accept new help and volunteer offers online. Please check back later.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <fieldset>
                <legend className="block text-lg font-medium text-slate-200 mb-3">
                  Select all that apply:
                </legend>
                <div className="space-y-2" role="group" aria-label="Ways you can help">
                  {helpOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center space-x-3 cursor-pointer hover:bg-white/5 p-3 rounded border border-transparent hover:border-white/10 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={helpTypes.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                        className="w-5 h-5 text-hope-green border-gray-300 rounded focus:ring-hope-green"
                      />
                      <span className="text-base text-slate-200">{option}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div>
              <label htmlFor="name" className="block text-lg font-medium text-slate-200 mb-2">
                Name (Optional)
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                className={inputClass}
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-lg font-medium text-slate-200 mb-2">
                Phone (Optional)
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={30}
                className={inputClass}
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-lg font-medium text-slate-200 mb-2">
                Email (Optional)
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={100}
                className={inputClass}
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label htmlFor="event" className="block text-lg font-medium text-slate-200 mb-2">
                Which Event? (Optional)
              </label>
              <select
                id="event"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                className={inputClass}
              >
                <option value="">Select an event...</option>
                <option value="general">General Help</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>

            {selectedEvent && (
              <div>
                <label htmlFor="specificHelp" className="block text-lg font-medium text-slate-200 mb-2">
                  How specifically for this event?
                </label>
                <textarea
                  id="specificHelp"
                  value={specificHelp}
                  onChange={(e) => setSpecificHelp(e.target.value)}
                  rows="4"
                  maxLength={1000}
                  className={`${inputClass} resize-none`}
                  placeholder="Tell us how you can help with this specific event..."
                />
              </div>
            )}

            <div>
              <label htmlFor="details" className="block text-lg font-medium text-slate-200 mb-2">
                Details (e.g., availability, preferred area)
              </label>
              <textarea
                id="details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows="5"
                maxLength={2000}
                className={`${inputClass} resize-none`}
                placeholder="Tell us more about how you can help..."
              />
            </div>

            <p className="text-sm text-slate-500 leading-relaxed">
              Contact info is optional and only used for coordination. You do not have to provide a name.
              Read our{' '}
              <Link to="/privacy" className="text-banana-yellow hover:underline font-semibold">
                Privacy Policy
              </Link>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 cyber-btn-yellow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {submitting ? 'Submitting...' : 'Submit Offer'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 uppercase text-sm tracking-wide"
              >
                Cancel
              </button>
            </div>
          </form>
          )}
        </div>
      </main>

      <Footer />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default HelpForm;
